import {
  EmploymentAtReference,
  IIdentityBlock,
  IManagerCandidate,
  IManualIdentityPreview,
} from "@/@types/divergence";

import { MANUAL_IDENTITY_LABELS } from "./messages";

interface IdentityBlockPayload {
  messages?: string[];
  message?: string;
  identity_failure?: string;
  manual_identity_allowed?: boolean;
  identity_candidates?: IManagerCandidate[];
  known_operator?: IManagerCandidate | null;
}

const payloadOf = (error: unknown): IdentityBlockPayload | null => {
  const data = (error as { data?: unknown })?.data;

  if (!data || typeof data !== "object") return null;

  return data as IdentityBlockPayload;
};

export const CPF_DIGITS: number = 11;

export const onlyDigits = (value: string): string => value.replace(/\D/g, "");

interface IdentityChoice {
  candidates: IManagerCandidate[];
  preview: IManualIdentityPreview | null;
  chosen: string;
  acknowledgedFor: string;
}

export const identityChoiceIsComplete = ({
  candidates,
  preview,
  chosen,
  acknowledgedFor,
}: IdentityChoice): boolean => {
  if (candidates.length > 0 && !chosen) return false;

  if (!preview) return false;

  const notice: EmploymentNotice | null = employmentNoticeOf(
    preview.employment,
  );

  if (notice?.tone !== "warning") return true;

  return acknowledgedFor === preview.identity.cpf;
};

export const IDENTITY_REPLACEMENT = "IDENTITY_REPLACED";

export const confirmInformedLabel = (
  hasCandidates: boolean,
  candidatesFailed: boolean,
): string =>
  hasCandidates && !candidatesFailed
    ? MANUAL_IDENTITY_LABELS.CONFIRM_CANDIDATE
    : MANUAL_IDENTITY_LABELS.CONFIRM;

export const identityBlockOf = (error: unknown): IIdentityBlock | null => {
  const payload: IdentityBlockPayload | null = payloadOf(error);

  if (!payload?.identity_failure) return null;

  if (typeof payload.manual_identity_allowed !== "boolean") return null;

  return {
    identity_failure: payload.identity_failure,
    manual_identity_allowed: payload.manual_identity_allowed,
    message: payload.messages?.[0] ?? payload.message ?? "",
    candidates: payload.identity_candidates ?? [],
    knownOperator: payload.known_operator ?? null,
  };
};

export interface EmploymentNotice {
  tone: "warning" | "info";
  text: string;
}

const NOTICE_BY_EMPLOYMENT: Record<
  EmploymentAtReference,
  EmploymentNotice | null
> = {
  EMPLOYED: null,
  TERMINATED_BEFORE: {
    tone: "warning",
    text: MANUAL_IDENTITY_LABELS.NOTICE.TERMINATED_BEFORE,
  },
  NOT_YET_ADMITTED: {
    tone: "warning",
    text: MANUAL_IDENTITY_LABELS.NOTICE.NOT_YET_ADMITTED,
  },
  UNKNOWN: {
    tone: "info",
    text: MANUAL_IDENTITY_LABELS.NOTICE.UNKNOWN,
  },
};

export const employmentNoticeOf = (
  employment: EmploymentAtReference,
): EmploymentNotice | null => NOTICE_BY_EMPLOYMENT[employment] ?? null;
